import { fetchEventSource } from '@microsoft/fetch-event-source'
import { AxiosError } from 'axios'
import { EventBus } from 'features/cantileverAISystem/components/eventBus'
import { AIPromptEvent } from 'features/cantileverAISystem/components/makeAiPromptEventDef'
import z from 'zod'

const EntryDataSchema = z.record(z.string(), z.unknown())

const ToolResultEntriesContentSchema = z.object({
  entries: z.array(EntryDataSchema),
})

const PineconeResultSchema = z
  .object({
    embedding: z.array(z.number()).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    score: z.number().optional(),
    re_rank_used: z.boolean().optional(),
  })
  .catchall(z.unknown())

const ToolResultEmbeddingsContentSchema = z
  .object({
    success: z.boolean().optional(),
    results: z.array(PineconeResultSchema),
  })
  .catchall(z.unknown())

const ToolResultContentSchema = z.union([
  ToolResultEntriesContentSchema,
  ToolResultEmbeddingsContentSchema,
])

const BaseStreamingEventSchema = z.object({
  session_id: z
    .string()
    .meta({ description: 'Session ID for the streaming conversation' }),
})

const StreamingThoughtEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('thought').meta({ description: 'Event type identifier' }),
  content: z.string().meta({ description: 'The thought text from the LLM' }),
})

const StreamingToolCallEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('tool_call').meta({ description: 'Event type identifier' }),
  function_name: z
    .string()
    .meta({ description: 'Name of the function being called' }),
  content: z
    .string()
    .meta({ description: 'Description of the tool call with arguments' }),
})

const StreamingToolResultEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('tool_result').meta({ description: 'Event type identifier' }),
  function_name: z
    .string()
    .meta({ description: 'Name of the function that was called' }),
  content: ToolResultContentSchema.meta({
    description: 'The response content from the tool execution',
  }),
})

const StreamingErrorEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('error').meta({ description: 'Event type identifier' }),
  error: z.string().meta({ description: 'Error message or description' }),
})

export const OnStreamEventSchema = z.union([
  StreamingThoughtEventSchema,
  StreamingToolCallEventSchema,
  StreamingToolResultEventSchema,
  StreamingErrorEventSchema,
])

export class AiControllerV3 {
  constructor(
    private eventBus: EventBus,
    private authorization?: string,
    private LLM_SERVICE?: string,
    private userId?: string
  ) {}

  setConfig({
    authorization,
    LLM_SERVICE,
    userId,
  }: Partial<{
    authorization: string
    LLM_SERVICE: string
    userId: string
  }>) {
    this.authorization = authorization
    this.LLM_SERVICE = LLM_SERVICE
    this.userId = userId
  }

  async deprecated_handleAIPromptEvent(promptEvent: AIPromptEvent) {
    const prompt = this.constructPrompt(promptEvent)

    try {
      const { response: responseEvents, sessionId } =
        await this.sendDelphiQuery({
          sessionId: promptEvent.sessionId,
          prompt,
          promptEvent,
        })

      for (const event of responseEvents || []) {
        this.eventBus.send({
          ...event,
          sessionId,
        })
      }
    } catch (error) {
      if (error instanceof Error) {
        promptEvent.onError?.(error.message)
      } else if (error instanceof z.ZodError) {
        promptEvent.onError?.(error.message)
      } else if (error instanceof AxiosError) {
        promptEvent.onError?.(error.message)
      }
    }
  }

  constructPrompt(event: AIPromptEvent) {
    // Remove availableResponseEvents from the event to avoid sending it to the LLM
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { availableResponseEvents, eventSchema, ...promptEvent } = event

    return `
      A prompt event was fired from a frontend React component.

      <prompt_event>
        ${JSON.stringify(promptEvent)}
      </prompt_event>

      The prompt event was generated using the following schema:

      <prompt_event_schema>
        ${JSON.stringify(z.toJSONSchema(event.eventSchema))}
      </prompt_event_schema>

      You will fire one or more events in response to the prompt event.
    `
  }

  async sendDelphiQuery({
    sessionId,
    prompt,
    promptEvent,
    systemPrompt,
  }: {
    sessionId?: string
    prompt: string
    promptEvent: AIPromptEvent
    systemPrompt?: string
  }) {
    const aiResponseSchema = this.getAIResponseSchema(promptEvent)
    const request = {
      app_name: 'default_engine',
      user_id: this.userId,
      session_id: sessionId,
      new_message: {
        role: 'user',
        parts: [{ text: prompt }],
      },
      system_prompt: systemPrompt,
      response_schema: z.toJSONSchema(aiResponseSchema),
    }

    const StreamingResponseEventSchema = BaseStreamingEventSchema.extend({
      type: z
        .literal('response')
        .meta({ description: 'Event type identifier' }),
      content: aiResponseSchema,
    })

    const StreamingEventSchema = z.union([
      OnStreamEventSchema,
      StreamingResponseEventSchema,
    ])

    const endpoint = `${this.LLM_SERVICE}/api/v3/delphi/query`
    let responseEvents: z.infer<typeof StreamingResponseEventSchema> | undefined
    await fetchEventSource(endpoint, {
      method: 'POST',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${this.authorization}`,
      },
      onmessage(rawStreamingEvent) {
        const parseResult = StreamingEventSchema.safeParse(
          JSON.parse(rawStreamingEvent.data)
        )

        if (!parseResult.success) {
          return
        }

        const parsedStreamingEvent = parseResult.data
        if (parsedStreamingEvent.type === 'response') {
          responseEvents = parsedStreamingEvent
        } else if (parsedStreamingEvent.type === 'error') {
          promptEvent.onError?.(parsedStreamingEvent.error)
        } else {
          promptEvent.onStream?.(parsedStreamingEvent)
        }
      },
      onerror(error) {
        promptEvent.onError?.(error.message)
      },
    })

    return {
      response: responseEvents?.content,
      sessionId: responseEvents?.session_id,
    }
  }

  getAIResponseSchema(event: AIPromptEvent) {
    return z.array(z.union(event.availableResponseEvents))
  }
}
